import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
// Constants - Functions
import { WEB_SERVICE_MIMS, graphicDataColor1 } from "../../../../config/constants";
// Components
import { MainDiv } from "./styles";
import UseFetch from "../../../../config/useFetch";
import Loader from "../../../loader";
import Title from "../../../title";
import RecentTreatmentEfficacy from "./sections/recentTreatmentEfficacy";
import MetricPlot from "./sections/metricPlot";

const TreatmentEfficacy = () => {
  const [metricsPerWeek, setMetricsPerWeek] = useState([]);
  const [lastMetric, setLastMetric] = useState([]);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(true);
  const [DM_Dataset, setDM_Dataset] = useState({});
  const [adherence_Dataset, setAdherence_Dataset] = useState({});
  const [competence_Dataset, setCompetence_Dataset] = useState({});
  const { patientId } = useParams();

  const getLastNonEmptyArray = (arrays) => {
    for (let i = arrays.length - 1; i >= 0; i--) {
      if (Array.isArray(arrays[i]) && arrays[i].length > 0) {
        if (arrays[i][0].ppf) {
          return arrays[i];
        }
      }
    }
    return [];
  };

  // Fetch metrics
  useEffect(() => {
    UseFetch("get", `${WEB_SERVICE_MIMS}/recsys/get-treatment-efficacy/${patientId}`)
      .then(({ data }) => {
        setMetricsPerWeek(data);
        setLastMetric(getLastNonEmptyArray(data));
        setIsLoadingMetrics(false);
      })
      .catch((error) => {
        console.log("Get metrics: ", error);
      });

    return () => {
      setMetricsPerWeek([]);
      setLastMetric([]);
      setIsLoadingMetrics(true);
    };
  }, [patientId]);

  // Set datasets
  useEffect(() => {
    const getMetricsValuePerWeek = (metricKey) => {
      return metricsPerWeek.map((metricsArray) => {
        const metrics = metricsArray.filter((metric) => Boolean(metric?.[metricKey]));
        if (!metrics.length) return NaN;
        const metricsSum = metrics.reduce((accumulator, metric) => accumulator + Number(metric[metricKey]), 0);
        return metricsSum / metrics.length;
      });
    };

    const getAdherenceMetricsPerWeek = () => {
      return metricsPerWeek.map((metricsArray) => {
        const metrics = metricsArray.filter((metric) => Boolean(metric?.recent_adherence));
        if (!metrics.length) return NaN;
        const metricsSum = metrics.reduce((accumulator, metric) => accumulator + Number(metric.recent_adherence), 0);
        return Math.round((metricsSum / metrics.length) * 100);
      });
    };

    const DM_data = {
      labels: metricsPerWeek.map((_, i) => i + 1),
      datasets: [
        {
          label: "Difficulty Modulators",
          data: getMetricsValuePerWeek("delta_dm"),
          backgroundColor: [graphicDataColor1],
          borderColor: [graphicDataColor1],
        },
      ],
    };

    const adherence_data = {
      labels: metricsPerWeek.map((_, i) => i + 1),
      datasets: [
        {
          label: "Adherence Mean (%)",
          data: getAdherenceMetricsPerWeek(),
          backgroundColor: [graphicDataColor1],
          borderColor: [graphicDataColor1],
        },
      ],
    };

    const competence_data = {
      labels: metricsPerWeek.map((_, i) => i + 1),
      datasets: [
        {
          label: "Competence",
          data: getMetricsValuePerWeek("ppf"),
          backgroundColor: [graphicDataColor1],
          borderColor: [graphicDataColor1],
        },
      ],
    };

    setDM_Dataset(DM_data);
    setAdherence_Dataset(adherence_data);
    setCompetence_Dataset(competence_data);

    return () => {
      setDM_Dataset({});
      setAdherence_Dataset({});
      setCompetence_Dataset({});
    };
  }, [metricsPerWeek]);

  const getMetricPerWeekAndDay = (metricsPerWeek, key, day) => {
    const metrics = metricsPerWeek.filter((metric) => metric?.[key] && metric.WEEKDAY === day);

    if (!metrics.length) return 0;

    const metricsSum = metrics.reduce((accumulator, metrics) => accumulator + Number(metrics[key]), 0);

    const average = key === "adherence" ? Math.round((metricsSum / metrics.length) * 100) : metricsSum / metrics.length;

    return key === "adherence" ? `${average}%` : average.toString().length > 5 ? average.toString().substring(0, 5) : average;
  };

  const keyMapping = {
    "Difficulty Modulators": "delta_dm",
    "Adherence Mean (%)": "adherence",
    Competence: "ppf",
  };

  const options = {
    clip: false,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        labels: {
          usePointStyle: true,
          filter: (legendItem, chartData) => {
            let entries = chartData.datasets.map((e) => e.label);
            return entries.indexOf(legendItem.text) === legendItem.datasetIndex;
          },
        },
      },
      tooltip: {
        displayColors: false,
        callbacks: {
          title: (tooltipItems) => {
            const label = tooltipItems[0].dataset.label;
            if (label === "Difficulty Modulators") return `Weekly DM Change → ${tooltipItems[0].formattedValue}`;
            if (label === "Adherence Mean (%)") return `Weekly adherence → ${tooltipItems[0].formattedValue}%`;
            if (label === "Competence") return `Weekly Competence → ${tooltipItems[0].formattedValue}`;
            return tooltipItems[0].dataset.label;
          },
          label: (tooltipItem) => {
            const metrics = metricsPerWeek[tooltipItem.dataIndex];
            const key = keyMapping[tooltipItem.dataset.label];

            const monday = getMetricPerWeekAndDay(metrics, key, "MONDAY");
            const tuesday = getMetricPerWeekAndDay(metrics, key, "TUESDAY");
            const wednesday = getMetricPerWeekAndDay(metrics, key, "WEDNESDAY");
            const thursday = getMetricPerWeekAndDay(metrics, key, "THURSDAY");
            const friday = getMetricPerWeekAndDay(metrics, key, "FRIDAY");
            const saturday = getMetricPerWeekAndDay(metrics, key, "SATURDAY");
            const sunday = getMetricPerWeekAndDay(metrics, key, "SUNDAY");

            return [
              `Monday → ${monday}`,
              `Tuesday → ${tuesday}`,
              `Wednesday → ${wednesday}`,
              `Thursday → ${thursday}`,
              `Friday → ${friday}`,
              `Saturday → ${saturday}`,
              `Sunday → ${sunday}`,
            ];
          },
        },
      },
    },
  };

  return (
    <>
      <Title title={"TREATMENT EFFICACY"} marginBottom="15px" />
      {isLoadingMetrics ? (
        <Loader height={50} />
      ) : (
        <MainDiv>
          <RecentTreatmentEfficacy lastMetric={lastMetric} />
          <MetricPlot data={DM_Dataset} title="Difficulty Modulators" subtitle="(DM Changes)" axisY="DM Change" options={options} />
          <MetricPlot data={adherence_Dataset} title="Adherence" axisY="Adherence Mean (%)" max={100} options={options} />
          <MetricPlot data={competence_Dataset} title="Competence" axisY="Competence" max={1} options={options} />
        </MainDiv>
      )}
    </>
  );
};

export default TreatmentEfficacy;
